import { useCallback, useEffect, useState } from "react";
import { BASE_PATH, withBase } from "./base";

export const PAGES = ["home", "today", "curriculum", "mastery", "portfolio", "reports", "family", "subscription", "suggestions", "activities", "competencies", "learners", "matching", "review"] as const;
export type Page = (typeof PAGES)[number];

export function pageFromPath(pathname: string): Page {
  const path = pathname.replace(/\/+$/, "");
  if (path === BASE_PATH) return "home";
  if (!path.startsWith(`${BASE_PATH}/`)) return "home";
  const slug = path.slice(BASE_PATH.length + 1);
  return PAGES.find(page => page === slug) ?? "home";
}

export function pageUrl(page: Page) {
  return withBase(page === "home" ? "/" : `/${page}`);
}

export function canOpenPage(page: Page, role: string) {
  if (role === "admin" || role === "manager") return true;
  if (["activities", "competencies", "learners", "matching", "review"].includes(page)) return false;
  return role !== "teacher" || !["family", "subscription", "suggestions"].includes(page);
}

export function usePageNavigation() {
  const [page, setPage] = useState<Page>(() => pageFromPath(window.location.pathname));
  useEffect(() => {
    const restore = () => setPage(pageFromPath(window.location.pathname));
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  const navigate = useCallback((next: Page, replace = false) => {
    const href = pageUrl(next);
    if (window.location.pathname !== href) {
      window.history[replace ? "replaceState" : "pushState"](null, "", href);
    }
    setPage(next);
  }, []);
  return [page, navigate] as const;
}
